import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  Tooltip,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { Customer } from '@/models/customer';
import './SmartCustomerForm.css';

export interface SmartCustomerFormProps {
  customers: Customer[];
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer) => void;
  onCustomersRefresh?: () => Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
}

const SmartCustomerForm: React.FC<SmartCustomerFormProps> = ({
  customers,
  selectedCustomer,
  onCustomerSelect,
  onCustomersRefresh,
  disabled = false,
  loading = false,
  error
}) => {
  // UI states
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: Customer | null) => {
    console.log('handleCustomerSelect called with:', customer);
    if (customer) {
      onCustomerSelect(customer);
      showNotification('Customer selected successfully', 'success');
    }
  };

  // Format address
  const formatAddress = (customer: Customer) => {
    const parts = [
      customer.address_1,
      customer.address_2,
      customer.address_3,
      customer.address_4,
      customer.pin
    ].filter(Boolean);
    return parts.join(', ');
  };


  return (
    <Box className="smart-customer-form">
      {/* Header */}
      <Box className="scf-form-header">
        <Box className="scf-header-left">
          <Typography variant="h6" className="scf-form-title">
            Select Customer
          </Typography>
        </Box>
        <Box className="scf-header-actions">
          <Tooltip title="Refresh Customers">
            <IconButton
              size="small"
              onClick={() => onCustomersRefresh?.()}
              disabled={disabled || loading}
              className="scf-header-icon-button"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" className="scf-error-alert">
          {error}
        </Alert>
      )}

      {/* Form Content */}
      <Box className="scf-form-content">
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 150 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Customer Selection */}
            <Box className="scf-customer-search-section">
          <Autocomplete
            options={(() => {
              const uniqueCustomers = new Map<string, { value: string; label: string; customer: Customer }>();
              customers.forEach(c => {
                if (c.name && !uniqueCustomers.has(c.name)) {
                  uniqueCustomers.set(c.name, { 
                    value: c._id, 
                    label: c.name, 
                    customer: c 
                  });
                }
              });
              return Array.from(uniqueCustomers.values());
            })()}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.label || '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === 'string' && typeof value === 'string') return option === value;
              if (typeof option === 'object' && typeof value === 'object') {
                return option.value === value.value;
              }
              return false;
            }}
            value={(() => {
              if (!selectedCustomer) return null;
              const uniqueCustomers = new Map<string, { value: string; label: string; customer: Customer }>();
              customers.forEach(c => {
                if (c.name && !uniqueCustomers.has(c.name)) {
                  uniqueCustomers.set(c.name, { 
                    value: c._id, 
                    label: c.name, 
                    customer: c 
                  });
                }
              });
              return uniqueCustomers.get(selectedCustomer.name) || null;
            })()}
            onChange={(_, option) => {
              if (option && typeof option === 'object') {
                handleCustomerSelect(option.customer);
              } else {
                handleCustomerSelect(null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer *"
                placeholder="Search and select customer..."
                error={!!validationErrors.customer}
                helperText={validationErrors.customer}
                disabled={disabled || submitting || loading}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: selectedCustomer && (
                    <InputAdornment position="end">
                      <CheckIcon color="success" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" {...otherProps} key={option.value}>
                  <Box>
                    <Typography variant="body2">
                      {option.label}
                    </Typography>
                    {option.customer && (
                      <Typography variant="caption" color="text.secondary">
                        {formatAddress(option.customer)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            freeSolo
            selectOnFocus
            clearOnBlur
          />
        </Box>

        {/* Customer Details - Address Only */}
        {selectedCustomer && (
          <Box className="scf-customer-details-display">
            <Typography variant="caption" className="scf-customer-address">
              {formatAddress(selectedCustomer)}
            </Typography>
          </Box>
        )}
          </>
        )}
      </Box>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SmartCustomerForm; 